/**
 * Content Generation Mixpanel Tracking
 * 
 * Centralized tracking for all Content Generation user interactions.
 * Page views are automatically tracked via App.jsx trackRouteChange.
 * This module handles specific user actions and events.
 */

import { track } from 'src/utils/mixpanel';
import { store } from 'src/main';
import _get from 'lodash/get';

// Event name prefix for all Content Generation events
const EVENT_PREFIX = 'Content Gen';

/**
 * Get common tracking properties from Redux state
 */
const getCommonProps = () => {
  const state = store.getState();
  return {
    userEmail: _get(state, ['user', 'config', 'profile', 'email']),
    beaconClientId: _get(state, ['user', 'config', 'vendor', 'BeaconClientId']),
    retailerId: _get(state, ['retailer', 'id']),
    companyId: _get(state, ['user', 'session', 'companyId'])
  };
};

// ============================================
// Profile Events
// ============================================

export const trackProfileCreated = (profileData: {
  profileId?: string;
  profileName?: string;
  templateUsed?: boolean;
}) => {
  track(`${EVENT_PREFIX} - Profile Created`, {
    ...getCommonProps(),
    ...profileData
  });
};

export const trackProfileUpdated = (profileData: {
  profileId?: string;
  profileName?: string;
  fieldsUpdated?: string[];
}) => {
  track(`${EVENT_PREFIX} - Profile Updated`, {
    ...getCommonProps(),
    ...profileData
  });
};

export const trackProfileDeleted = (profileData: {
  profileId?: string;
  profileName?: string;
}) => {
  track(`${EVENT_PREFIX} - Profile Deleted`, {
    ...getCommonProps(),
    ...profileData
  });
};

export const trackProfileDuplicated = (profileData: {
  sourceProfileId?: string;
  newProfileId?: string;
  profileName?: string;
}) => {
  track(`${EVENT_PREFIX} - Profile Duplicated`, {
    ...getCommonProps(),
    ...profileData
  });
};

export const trackProfileViewed = (profileData: {
  profileId?: string;
  profileName?: string;
}) => {
  track(`${EVENT_PREFIX} - Profile Viewed`, {
    ...getCommonProps(),
    ...profileData
  });
};

// ============================================
// Generation Events
// ============================================

export const trackGenerationStarted = (data: {
  skuCount: number;
  profileId?: string;
  profileName?: string;
  contentTypes?: string[];
}) => {
  track(`${EVENT_PREFIX} - Generation Started`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackGenerationCompleted = (data: {
  skuCount: number;
  profileId?: string;
  generationId?: string;
  durationMs?: number;
}) => {
  track(`${EVENT_PREFIX} - Generation Completed`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackGenerationFailed = (data: {
  skuCount?: number;
  profileId?: string;
  errorMessage?: string;
}) => {
  track(`${EVENT_PREFIX} - Generation Failed`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackProductSelected = (data: {
  productCount: number;
  selectionMethod: 'individual' | 'bulk' | 'filter' | 'all';
}) => {
  track(`${EVENT_PREFIX} - Products Selected`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackProfileAssigned = (data: {
  profileId?: string;
  profileName?: string;
  productCount: number;
}) => {
  track(`${EVENT_PREFIX} - Profile Assigned`, {
    ...getCommonProps(),
    ...data
  });
};

// ============================================
// Review Events
// ============================================

export const trackReviewStarted = (data: {
  reviewType: 'single' | 'project' | 'library';
  skuCount?: number;
  generationId?: string;
}) => {
  track(`${EVENT_PREFIX} - Review Started`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackContentEdited = (data: {
  contentType: 'title' | 'description' | 'bullets' | 'keywords';
  generationId?: string;
  sku?: string;
}) => {
  track(`${EVENT_PREFIX} - Content Edited`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackContentApproved = (data: {
  skuCount: number;
  generationId?: string;
  approvalType: 'individual' | 'bulk';
}) => {
  track(`${EVENT_PREFIX} - Content Approved`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackContentRejected = (data: {
  skuCount: number;
  generationId?: string;
  rejectionReason?: string;
}) => {
  track(`${EVENT_PREFIX} - Content Rejected`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackRegenerateRequested = (data: {
  sku?: string;
  contentType?: string;
  generationId?: string;
}) => {
  track(`${EVENT_PREFIX} - Regenerate Requested`, {
    ...getCommonProps(),
    ...data
  });
};

// ============================================
// Library Events
// ============================================

export const trackLibraryViewed = (data?: {
  filterApplied?: boolean;
  totalItems?: number;
}) => {
  track(`${EVENT_PREFIX} - Library Viewed`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackLibraryExport = (data: {
  exportFormat: 'csv' | 'excel' | 'pdf';
  itemCount: number;
  exportType: 'selected' | 'all' | 'filtered';
}) => {
  track(`${EVENT_PREFIX} - Library Export`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackLibraryFiltered = (data: {
  filterType: string;
  filterValue?: string;
}) => {
  track(`${EVENT_PREFIX} - Library Filtered`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackLibraryItemOpened = (data: {
  sku?: string;
  itemId?: string;
}) => {
  track(`${EVENT_PREFIX} - Library Item Opened`, {
    ...getCommonProps(),
    ...data
  });
};

// ============================================
// Project Events
// ============================================

export const trackProjectCreated = (data: {
  projectId?: string;
  projectName?: string;
  productCount?: number;
}) => {
  track(`${EVENT_PREFIX} - Project Created`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackProjectUpdated = (data: {
  projectId?: string;
  projectName?: string;
  updateType: 'settings' | 'products' | 'status';
}) => {
  track(`${EVENT_PREFIX} - Project Updated`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackProjectDeleted = (data: {
  projectId?: string;
  projectName?: string;
}) => {
  track(`${EVENT_PREFIX} - Project Deleted`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackProductsAddedToProject = (data: {
  projectId?: string;
  productCount: number;
  addMethod: 'search' | 'upload' | 'filter';
}) => {
  track(`${EVENT_PREFIX} - Products Added to Project`, {
    ...getCommonProps(),
    ...data
  });
};

// ============================================
// Navigation / UI Events
// ============================================

export const trackTabChanged = (data: {
  fromTab?: string;
  toTab: string;
}) => {
  track(`${EVENT_PREFIX} - Tab Changed`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackModalOpened = (data: {
  modalName: string;
  context?: string;
}) => {
  track(`${EVENT_PREFIX} - Modal Opened`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackModalClosed = (data: {
  modalName: string;
  action: 'submit' | 'cancel' | 'close';
}) => {
  track(`${EVENT_PREFIX} - Modal Closed`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackButtonClicked = (data: {
  buttonName: string;
  location: string;
  context?: Record<string, any>;
}) => {
  track(`${EVENT_PREFIX} - Button Clicked`, {
    ...getCommonProps(),
    ...data
  });
};

// ============================================
// Comparison Events
// ============================================

export const trackComparisonViewed = (data: {
  comparisonType: 'current_vs_generated' | 'version_comparison';
  sku?: string;
}) => {
  track(`${EVENT_PREFIX} - Comparison Viewed`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackVersionSelected = (data: {
  versionId?: string;
  versionNumber?: number;
  sku?: string;
}) => {
  track(`${EVENT_PREFIX} - Version Selected`, {
    ...getCommonProps(),
    ...data
  });
};

// ============================================
// Credit Events
// ============================================

export const trackCreditBalanceViewed = (data: {
  currentBalance?: number;
}) => {
  track(`${EVENT_PREFIX} - Credit Balance Viewed`, {
    ...getCommonProps(),
    ...data
  });
};

export const trackCreditsUsed = (data: {
  creditsUsed: number;
  action: string;
  remainingBalance?: number;
}) => {
  track(`${EVENT_PREFIX} - Credits Used`, {
    ...getCommonProps(),
    ...data
  });
};
